import styles from "./EstimateStep3.module.scss";
import classNames from "classnames/bind";
import BaseUploadFileV2 from "~/components/common/base-upload-file-v2/BaseUploadFileV2";
import FormInput from "~/components/common/form/formInput/FormInput";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { DragIcon } from "~/assets/icons";
import { EstimateModeViewEnum, StyleInspectionEnum, StyleInspectionOptions } from "~/pages/admin/jobs/add-job/constants/add-estimate.constants";
import BaseBoxContent from "~/components/common/base-box-content/BaseBoxContent";
import DroppableArea from "~/components/common/drop-drag/DroppableArea";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import AddItem from "~/components/common/add-item/AddItem";

const cx = classNames.bind(styles);
interface EstimateStep3Props {
    onChange: (formData: any) => void,
    data: any,
    mode: string
    errorForm: any,
    onPreview: () => void,
}

const normalizeStyle = (style: unknown) => {
    const value = Number(style);
    return Number.isFinite(value) ? value : StyleInspectionEnum.STANDARD;
};

const getStyleLabel = (style: number) =>
    StyleInspectionOptions.find((o) => o.id === normalizeStyle(style))?.label ?? "Standard";

const getStyleClass = (style: number) => {
    switch (normalizeStyle(style)) {
        case StyleInspectionEnum.SIDE_BY_SIDE:
            return "layout-side-by-side";
        case StyleInspectionEnum.WIDE:
            return "layout-wide";
        case StyleInspectionEnum.FULL:
            return "layout-full";
        case StyleInspectionEnum.STANDARD:
        default:
            return "layout-standard";
    }
};

const getPhotoFieldsStyle = (style: number): CSSProperties => {
    const s = normalizeStyle(style);
    if (s === StyleInspectionEnum.FULL) {
        return { display: "flex", flexDirection: "column", gap: 12, width: "100%" };
    }
    if (s === StyleInspectionEnum.WIDE) {
        return { display: "flex", flexDirection: "row", alignItems: "stretch", gap: 16, width: "100%" };
    }
    if (s === StyleInspectionEnum.SIDE_BY_SIDE) {
        return { display: "flex", flexDirection: "row", alignItems: "stretch", gap: 16, width: "100%" };
    }
    return { display: "flex", flexDirection: "row", alignItems: "stretch", gap: 16, width: "100%" };
};

const getPhotoMediaStyle = (style: number): CSSProperties => {
    const s = normalizeStyle(style);
    if (s === StyleInspectionEnum.FULL) {
        return { width: "100%", flex: "none" };
    }
    if (s === StyleInspectionEnum.WIDE) {
        return { flex: "1 1 70%", maxWidth: "70%", minWidth: 0 };
    }
    if (s === StyleInspectionEnum.SIDE_BY_SIDE) {
        return { flex: "1 1 50%", minWidth: 0 };
    }
    return { flex: "0 0 40%", maxWidth: "40%", minWidth: 0 };
};

const getPhotoDescriptionStyle = (style: number): CSSProperties => {
    const s = normalizeStyle(style);
    if (s === StyleInspectionEnum.FULL) {
        return { width: "100%", flex: "none" };
    }
    if (s === StyleInspectionEnum.WIDE) {
        return { flex: "1 1 30%", maxWidth: "30%", minWidth: 0 };
    }
    if (s === StyleInspectionEnum.SIDE_BY_SIDE) {
        return { flex: "1 1 50%", minWidth: 0 };
    }
    return { flex: "1 1 auto", minWidth: 0 };
};

const getUploadHeight = (style: number) => {
    const s = normalizeStyle(style);
    if (s === StyleInspectionEnum.FULL) return 300;
    if (s === StyleInspectionEnum.WIDE) return 200;
    return 171;
};

const getDescriptionHeight = (style: number) => {
    const s = normalizeStyle(style);
    if (s === StyleInspectionEnum.FULL) return "88px";
    if (s === StyleInspectionEnum.WIDE) return "200px";
    return "171px";
};
const EstimateStep3 = ({
    onChange,
    data,
    mode,
    errorForm,
    onPreview
}: EstimateStep3Props) => {
    const refs = useRef<any[]>([]);
    const styleDropdownRef = useRef<HTMLDivElement>(null);

    const [formData, setFormData] = useState<any>(
        {
            titleStep3: data.titleStep3 ? data.titleStep3 : "",
            inspections: data.inspections ? data.inspections : []
        }
    );
    const [openStyleIndex, setOpenStyleIndex] = useState<number | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (styleDropdownRef.current && !styleDropdownRef.current.contains(event.target as Node)) {
                setOpenStyleIndex(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleUpdateData = (update: any, index: number) => {
        let data = { ...formData };
        data.inspections[index] = { ...data.inspections[index], ...update };
        onChange(data);
        setFormData(data)
    }

    const handleUpdatePhotoData = (update: any, index: number, indexPhoto: number) => {
        let data = { ...formData };
        data.inspections[index].images[indexPhoto] = { ...data.inspections[index].images[indexPhoto], ...update };
        onChange(data);
        setFormData(data)
    }

    const onAddInspection = () => {
        let data = { ...formData }
        data.inspections = [...data.inspections, {
            sectionTitle: 'Section',
            style: 0,
            images: [
                {
                    description: '',
                    file: '',
                    fileUrl: ''
                }
            ]

        }]
        setFormData(data);
        onChange(data);

    }

    const onRemoveSection = (index: number) => {
        let data = { ...formData };
        data.inspections.splice(index, 1);
        onChange(data);
        setFormData(data)
    }

    const onRemovePhoto = (index: number, indexImage: number) => {
        let data = { ...formData };
        if (data.inspections[index].images.length > 1) {
            data.inspections[index].images.splice(indexImage, 1);
        }
        onChange(data);
        setFormData(data)
    }

    const addPhoto = (index: number) => {
        let data = { ...formData };
        data.inspections[index].images.push({
            description: '',
            file: '',
            fileUrl: '',
        })
        onChange(data);
        setFormData(data)
    }

    const handleUpdateTitleData = (update: any) => {
        let data: any = { ...formData, ...update }
        onChange(data);
        setFormData(data)
    }

    return (<div className={cx('estimate-step-container')}>
        <div className={cx('header', 'flex items-center justify-between')}>
            <TitleEditor value={formData.titleStep3}
                name={""}
                isViewMode={mode == EstimateModeViewEnum.VIEW}
                placeholder={""}
                inputkey={""}
                onChange={(value) => {
                    handleUpdateTitleData({
                        ...formData,
                        titleStep3: value
                    })
                }}
                styleView={{
                    fontSize: "20px",
                    fontWeight: 700,
                }}
            />
            <div>
                <button onClick={onPreview} className={cx('btn-preview')}>
                    View Page
                </button>
            </div>
        </div>

        <div className={cx('content')}>

            {
                formData.inspections.map((data: any, index: any) => <div className="mb-[40px]" key={index}>
                    <BaseBoxContent>
                        <div data-slot="header">
                            <div className="w-full flex items-center justify-between h-[88px] px-[20px]">
                                <div>
                                    <TitleEditor value={data.sectionTitle}
                                        name={""}
                                        placeholder={""}
                                        isViewMode={mode == EstimateModeViewEnum.VIEW}
                                        inputkey={""}
                                        onChange={(value) => {
                                            handleUpdateData({ sectionTitle: value }, index)
                                        }}
                                        styleView={{
                                            fontSize: "18px"
                                        }}
                                    />

                                    <div className={cx('title-style', 'mt-[8px]')} ref={openStyleIndex === index ? styleDropdownRef : undefined}>
                                        <p className="text-[#FFFFFF]">Style: {getStyleLabel(data.style ?? 0)}</p>
                                        {mode != EstimateModeViewEnum.VIEW && (
                                            <div className={cx('style-change-wrap')}>
                                                <p
                                                    className={cx('style-change')}
                                                    onClick={() => setOpenStyleIndex(openStyleIndex === index ? null : index)}
                                                >
                                                    Change
                                                </p>
                                                {openStyleIndex === index && (
                                                    <ul className={cx('style-dropdown')}>
                                                        {StyleInspectionOptions.map((option) => (
                                                            <li key={option.id}>
                                                                <button
                                                                    type="button"
                                                                    className={cx('style-dropdown-item', {
                                                                        active: normalizeStyle(data.style ?? 0) === option.id,
                                                                    })}
                                                                    onClick={() => {
                                                                        handleUpdateData({ style: option.id }, index);
                                                                        setOpenStyleIndex(null);
                                                                    }}
                                                                >
                                                                    {option.label}
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {
                                    mode != EstimateModeViewEnum.VIEW &&
                                    <button className={cx('button-remove-section')} onClick={() => onRemoveSection(index)}>
                                        Remove
                                    </button>
                                }

                            </div>
                        </div>

                        <div data-slot="content">
                            <div className={`w-full p-[16px] ${cx("photos-wrap", getStyleClass(data.style ?? 0))}`}>
                                <DroppableArea data={data.images}
                                    onChange={(newArray: any[]) => {
                                        let data = { ...formData };
                                        data.inspections[index].images = newArray;
                                        onChange(data);
                                        setFormData(data)
                                    }}
                                    groupName={"sections"}
                                    renderComponent={(images, imageIndex) => {
                                        const styleValue = normalizeStyle(data.style ?? 0);
                                        return (<>
                                            <div className={cx("photo-row")}>
                                                {
                                                    mode != EstimateModeViewEnum.VIEW && <div className={cx('drag-button', 'cursor-pointer')}>
                                                        <DragIcon className={cx("drag-icon")} />
                                                    </div>
                                                }

                                                {
                                                    mode != EstimateModeViewEnum.VIEW ? (
                                                        <div
                                                            className={cx("photo-fields")}
                                                            style={getPhotoFieldsStyle(styleValue)}
                                                        >
                                                            <div
                                                                className={cx("photo-media")}
                                                                style={getPhotoMediaStyle(styleValue)}
                                                            >
                                                                <BaseUploadFileV2
                                                                    key={`upload-${styleValue}-${index}-${imageIndex}`}
                                                                    ref={(el) => (refs.current[index] = el)}
                                                                    url={images.fileUrl}
                                                                    isViewMode={mode == EstimateModeViewEnum.VIEW}
                                                                    height={getUploadHeight(styleValue)}
                                                                    onChange={function (data: any): void {
                                                                        handleUpdatePhotoData({
                                                                            ...images,
                                                                            file: data.file,
                                                                            fileUrl: data.fileUrl,
                                                                        }, index, imageIndex)
                                                                    }}
                                                                    errorMessage={
                                                                        errorForm.inspectionList &&
                                                                            errorForm.inspectionList[index] ?
                                                                            errorForm.inspectionList[index].file : ""}
                                                                />
                                                            </div>
                                                            <div
                                                                className={cx("photo-description")}
                                                                style={getPhotoDescriptionStyle(styleValue)}
                                                            >
                                                                <FormInput
                                                                    key={`desc-${styleValue}-${index}-${imageIndex}`}
                                                                    name={"inspectionDescription"}
                                                                    text={""}
                                                                    isViewMode={mode == EstimateModeViewEnum.VIEW}
                                                                    placeholder={"Descriptions...."}
                                                                    inputkey={"companyDescription"}
                                                                    value={images.description}
                                                                    inputType="textarea"
                                                                    height={getDescriptionHeight(styleValue)}
                                                                    onChange={(e) => {
                                                                        handleUpdatePhotoData({
                                                                            ...images,
                                                                            description: e.target.value
                                                                        }, index, imageIndex)
                                                                    }}
                                                                    error={
                                                                        errorForm.inspectionList &&
                                                                            errorForm.inspectionList[index] ?
                                                                            errorForm.inspectionList[index].description : ""}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (<>
                                                        <div className={cx('section-item-preview', getStyleClass(styleValue))}>
                                                            <div className={cx("section-item-description")}>
                                                                <div className={cx("section-item-title", 'mb-[8px]')}>Content</div>
                                                                <div className={cx("section-item-description-content")}>
                                                                    {images.description}
                                                                </div>
                                                            </div>
                                                            <div className={cx("section-item-media")}>
                                                                <div className={cx("section-item-title", 'mb-[8px]')}>Attachments</div>
                                                                {
                                                                    images.fileUrl ?
                                                                        <div className={cx("section-item-image")}>
                                                                            <img src={images.fileUrl} height={"100%"} />
                                                                        </div>
                                                                        :
                                                                        <div className={cx('no-image')}>
                                                                            No Image
                                                                        </div>
                                                                }

                                                            </div>
                                                        </div>


                                                    </>)
                                                }

                                                {
                                                    mode != EstimateModeViewEnum.VIEW && <div className={cx("photo-actions")}>
                                                        {
                                                            data.images.length > 1 && <Trash2Icon color="#FA6363" onClick={() => onRemovePhoto(index, imageIndex)} className="cursor-pointer" />
                                                        }
                                                    </div>
                                                }


                                            </div>
                                        </>)
                                    }}
                                />
                            </div>
                        </div>

                        {
                            mode != EstimateModeViewEnum.VIEW && <div data-slot="footer">
                                <div className="w-full px-[16px] py-[20px]">
                                    <AddItem onClick={() => addPhoto(index)} label="Add Photos" />
                                </div>
                            </div>
                        }

                    </BaseBoxContent>
                </div>)
            }

        </div>

        {
            mode != EstimateModeViewEnum.VIEW &&
            <button className={cx('add-section', 'mt-[32px]')} onClick={onAddInspection}>
                <PlusIcon />
                Add Section
            </button>
        }



        {errorForm.inspections && <p className={cx("error-message")}>{errorForm.inspections}</p>}
    </div>)
}

export default EstimateStep3;
